import { createInertiaApp } from "@inertiajs/react";
import { type ComponentType, createElement, type ReactNode } from "react";
import { createRoot } from "react-dom/client";
import BackofficeLayout from "@/layouts/backoffice-layout";
import { initI18n } from "@/lib/i18n";

// Temporary type definition, until @inertiajs/react provides one
type ResolvedComponent = {
  default: ComponentType & {
    layout?: (page: ReactNode) => ReactNode;
  };
};

createInertiaApp({
  // Set default page title
  // see https://inertia-rails.dev/guide/title-and-meta
  //
  // title: title => title ? `${title} - App` : 'App',

  resolve: async (name) => {
    const pages = import.meta.glob<ResolvedComponent>("../pages/**/*.tsx");
    const resolver = pages[`../pages/${name}.tsx`];

    if (!resolver) {
      console.error(`Missing Inertia page component: '${name}.tsx'`);
      throw new Error(`Missing page: ${name}`);
    }

    const page = await resolver();
    page.default.layout ||= (page) => createElement(BackofficeLayout, null, page);

    return page;
  },

  setup({ el, App, props }) {
    if (el) {
      const initialPage = props.initialPage as {
        props: {
          locale?: string;
          translations?: Record<string, unknown>;
        };
      };
      const locale = initialPage.props.locale || "es-AR";
      const translations = initialPage.props.translations || {};

      initI18n({ locale, translations });

      createRoot(el).render(createElement(App, props));
    } else {
      console.error(
        "Missing root element.\n\n" +
          "If you see this error, it probably means you load Inertia.js on non-Inertia pages.\n" +
          'Consider moving <%= vite_typescript_tag "inertia" %> to the Inertia-specific layout instead.',
      );
    }
  },
  defaults: {
    visitOptions: () => {
      return { viewTransition: true };
    },
  },
  progress: {
    delay: 250,
    color: "var(--color-primary)",
    includeCSS: true,
  },
});
